import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import LocalStorageButtons from '@components/LocalStorageButtons'
import FilterGridContainer from '@components/toolbar/FilterGridContainer'
import FilterGridItem from '@components/toolbar/FilterGridItem'
import { FilterMyList } from '@components/toolbar/FilterMyList'
import { FilterSelectGebied } from '@components/toolbar/FilterSelectGebied'
import type { FilterOption } from '@models/Filter'
import type { Gebied } from '@models/Gebied'

import { SelectObject } from '../../../../core/components/SelectObject'
import { SelectString } from '../../../../core/components/SelectString'
import { useLocalFilters } from '../../../../core/hooks/useLocalFilters'
import { queryKey } from '../../hooks/useKpi'
import type { FetchKpiFilters } from '../../types/FetchKpi'

const filterGebiedenByCoordinerende = (gebieden, coordinerende) =>
  gebieden.filter(
    (gebied) => gebied.coordinerende.indexOf(coordinerende) !== -1,
  )

interface Props {
  onChange: (filters: FetchKpiFilters) => void
  onRefresh: () => void
  onReset: () => void
  filters: FetchKpiFilters
  initialFilters: FetchKpiFilters
  filterOptions: {
    gebieden: Gebied[]
    netbeheerders: string[]
    coordinerendeAannemers: string[]
    labels: FilterOption[]
  }
}

export const KpisFilters = ({
  onChange,
  onRefresh,
  onReset,
  filters,
  initialFilters,
  filterOptions: { gebieden, netbeheerders, coordinerendeAannemers, labels },
}: Props) => {
  const { localFilters, handleFilterChange, handleZoeken, handleReset } =
    useLocalFilters({
      filters,
      initialFilters,
      onChange: (filters) => onChange(filters),
      onRefresh,
      onReset,
    })
  const { gebied, netbeheerder, coordinerende, myList } = localFilters
  const gebiedenByCoordinerenden = coordinerende
    ? filterGebiedenByCoordinerende(gebieden, coordinerende)
    : gebieden

  const handleCoordinerendeChange = (filter: {
    coordinerende: FetchKpiFilters['coordinerende']
  }) => {
    handleFilterChange({
      ...filter,
      gebied: filterGebiedenByCoordinerende(gebieden, filter.coordinerende),
    })
  }

  const handleGebiedChange = (filter: {
    gebied: FetchKpiFilters['gebied']
  }) => {
    handleFilterChange(
      filter.gebied.length === 0 ? { ...filter, coordinerende: null } : filter,
    )
  }

  return (
    <FilterGridContainer>
      <FilterGridItem>
        <SelectString
          options={coordinerendeAannemers}
          label="Coordinerende"
          value={coordinerende}
          onChange={(coordinerende) =>
            handleCoordinerendeChange({ coordinerende })
          }
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectGebied
          options={gebiedenByCoordinerenden}
          value={gebied}
          onFilterChange={({ gebied }) => handleGebiedChange({ gebied })}
        />
      </FilterGridItem>

      <FilterGridItem>
        <SelectString
          options={netbeheerders}
          label="Netbeheerder"
          value={netbeheerder}
          onChange={(netbeheerder) => handleFilterChange({ netbeheerder })}
        />
      </FilterGridItem>

      {labels.length > 0 && (
        <FilterGridItem>
          <SelectObject
            multiple
            options={labels}
            label="Labels"
            value={localFilters.labels}
            onChange={(labels) => handleFilterChange({ labels })}
          />
        </FilterGridItem>
      )}

      <FilterGridItem
        minColumns="full"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Tooltip title="Reset filters">
          <IconButton onClick={() => handleReset()}>
            <CloseIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Zoeken">
          <IconButton onClick={() => handleZoeken()}>
            <SearchIcon />
          </IconButton>
        </Tooltip>

        <FilterMyList myList={myList} onFilterChange={handleFilterChange} />

        <LocalStorageButtons
          name={queryKey}
          storable={localFilters}
          onClickLoad={handleFilterChange}
        />
      </FilterGridItem>
    </FilterGridContainer>
  )
}
